import { formatDate } from '~/prix/types/date'
import { formatAsBrNumber } from '~/prix/utils/types/number'
import { machineServiceStatus } from '../machineServiceObject'
import type { RuralReportTemplateProps } from '../../report/ruralReportTemplateTitle'

export const machineServiceReportTypeValues = [
  'pendingSchedule',
  'serviceRequests',
  'serviceExecutions',
] as const

export type MachineServiceReportType = (typeof machineServiceReportTypeValues)[number]

export interface MachineServiceReportFilters {
  reportType: MachineServiceReportType
  citizenId?: string | null
  communityId?: string | null
  serviceType?: string | null
  machineType?: string | null
  heavyMachineryId?: string | null
  operatorId?: string | null
  requestStartDate?: Date | string | null
  requestEndDate?: Date | string | null
  startDate?: Date | string | null
  endDate?: Date | string | null
  status?: string | null
}

export interface MachineServiceReportEntry {
  id: string
  code: number
  protocolCode: number | null
  requestedAt?: Date | string | null
  serviceDate?: Date | string | null
  startTime?: Date | string | null
  endTime?: Date | string | null
  citizenId: string
  citizenName: string
  communityId?: string | null
  communityName?: string | null
  residenceTitle?: string | null
  residenceAddress?: string | null
  serviceType: string
  status: string
  heavyMachineryId: string
  heavyMachineryTitle: string
  heavyMachineryType: string
  operatorId: string
  operatorName: string
  estimatedHours: number
  hourPrice: number
  totalPrice: number | null
}

export interface RuralMachineServiceReportProperties extends RuralReportTemplateProps {
  filters?: MachineServiceReportFilters
  data?: MachineServiceReportEntry[]
}

export const machineServiceReportTitleMap = {
  pendingSchedule: 'Cronograma de serviços pendentes',
  serviceRequests: 'Relatório de solicitações de serviços',
  serviceExecutions: 'Relatório de execuções de serviços',
} satisfies Record<MachineServiceReportType, string>

export const machineServiceReportBaseNameMap = {
  pendingSchedule: 'machine-service-pending-schedule-report',
  serviceRequests: 'machine-service-request-report',
  serviceExecutions: 'machine-service-execution-report',
} satisfies Record<MachineServiceReportType, string>

export const machineServiceReportColor = '#1E7A5E'

function extractDateParts(value?: Date | string | null) {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null
    }

    return {
      day: value.getUTCDate(),
      month: value.getUTCMonth() + 1,
      year: value.getUTCFullYear(),
    }
  }

  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)

  if (isoDateMatch) {
    return {
      year: Number(isoDateMatch[1]),
      month: Number(isoDateMatch[2]),
      day: Number(isoDateMatch[3]),
    }
  }

  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return {
    day: parsedDate.getUTCDate(),
    month: parsedDate.getUTCMonth() + 1,
    year: parsedDate.getUTCFullYear(),
  }
}

export function formatMachineServiceReportText(
  value?: string | null,
  emptyValue = 'Não informado',
) {
  return value?.trim() || emptyValue
}

export function formatMachineServiceReportDate(
  value?: Date | string | null,
  emptyValue = 'Não informado',
) {
  const dateParts = extractDateParts(value)

  if (!dateParts) {
    if (!value) {
      return emptyValue
    }

    return String(value)
  }

  const javascriptDate = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day))

  return formatDate(javascriptDate)
}

export function formatMachineServiceReportDateTime(
  value?: Date | string | null,
  emptyValue = 'Não informado',
) {
  if (!value) {
    return emptyValue
  }

  const parsedDate = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(parsedDate.getTime())) {
    return String(value)
  }

  return parsedDate.toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}

export function formatMachineServiceReportInteger(
  value?: number | string | null,
  emptyValue = 'Não informado',
) {
  if (value === null || value === undefined) {
    return emptyValue
  }

  const normalizedValue = Number(value)

  if (Number.isNaN(normalizedValue)) {
    return emptyValue
  }

  return formatAsBrNumber(normalizedValue, 0)
}

export function formatMachineServiceReportCurrency(
  value?: number | string | null,
  emptyValue = 'Não informado',
) {
  if (value === null || value === undefined) {
    return emptyValue
  }

  const normalizedValue = Number(value)

  if (Number.isNaN(normalizedValue)) {
    return emptyValue
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(normalizedValue)
}

export function formatMachineServiceReportStatus(
  status?: string | null,
  emptyValue = 'Não informado',
) {
  if (!status) {
    return emptyValue
  }

  const statusLabels: Record<string, string> = machineServiceStatus.keysAndLabels ?? {}

  return statusLabels[status] ?? status
}

export function normalizeMachineServiceReportDateInput(value?: Date | string | null) {
  if (!value) {
    return null
  }

  if (typeof value === 'string') {
    return value.includes('T') ? value.split('T')[0] : value
  }

  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export function buildMachineServiceReportPeriodLabel(
  startDate?: Date | string | null,
  endDate?: Date | string | null,
  emptyValue = 'Não informado',
) {
  const formattedStartDate = formatMachineServiceReportDate(startDate, '')
  const formattedEndDate = formatMachineServiceReportDate(endDate, '')

  if (!formattedStartDate && !formattedEndDate) {
    return emptyValue
  }

  if (formattedStartDate && formattedEndDate) {
    if (formattedStartDate === formattedEndDate) {
      return formattedStartDate
    }

    return `${formattedStartDate} até ${formattedEndDate}`
  }

  if (formattedStartDate) {
    return `A partir de ${formattedStartDate}`
  }

  return `Até ${formattedEndDate}`
}
